import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Submerge. Water with double-scrolling caustics ──────────────
//
// Production-grade water effect using the industry-standard
// double-scrolling caustic texture technique (Subnautica, Sea of
// Thieves, etc.), with several AAA polish passes layered on top:
//
//   1. Pre-rendered tileable Worley F2-F1 caustic texture (built
//      once at module load on a canvas).
//   2. Double-scrolling sample with `min()`. Moving cellular web.
//   3. Per-channel chromatic aberration on the caustic sampling
//      → iridescent rainbow fringing on the brightest veins.
//   4. Sparkle pinpoints at caustic intersection peaks. Gives
//      micro-stars that twinkle as the two scrolls drift.
//   5. Foam noise at the meniscus rim → surface-tension turbulence
//      so the edge isn't a clean smooth circle.
//   6. Wet darkening. Water absorbs light, so the underlying card
//      art reads slightly darker through the puddle.

const RISE_HEIGHT_OFFSET = 0.06;
const PLANE_W = CARD_WIDTH * 1.2;
const PLANE_H = CARD_HEIGHT * 1.2;

let _sharedCausticTexture = null;

/**
 * Generate a tileable caustic texture using Worley F2-F1.
 *
 * F2-F1 = "boundary distance." Small where you're equidistant from
 * two centres (cell walls), large at cell centres. Produces the
 * iconic caustic web pattern.
 *
 * Cell jitter is seeded by `cx % cellCount` (NOT cx directly) so
 * cells at the wrap boundary produce identical jitter on both sides
 * → seamless tiling.
 */
function getCausticTexture() {
  if (_sharedCausticTexture) return _sharedCausticTexture;

  const size = 256;
  const cellCount = 8;
  const cellSize = size / cellCount;

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  const cells = [];
  for (let cy = -1; cy <= cellCount; cy++) {
    for (let cx = -1; cx <= cellCount; cx++) {
      const wcx = ((cx % cellCount) + cellCount) % cellCount;
      const wcy = ((cy % cellCount) + cellCount) % cellCount;
      const seed = wcy * cellCount + wcx;
      const jx = ((seed * 9301 + 49297) % 233280) / 233280;
      const jy = ((seed * 49297 + 9301) % 233280) / 233280;
      cells.push({ x: (cx + jx) * cellSize, y: (cy + jy) * cellSize });
    }
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let f1 = Infinity;
      let f2 = Infinity;
      for (let i = 0; i < cells.length; i++) {
        const dx = x - cells[i].x;
        const dy = y - cells[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < f1) { f2 = f1; f1 = dist; }
        else if (dist < f2) { f2 = dist; }
      }
      const boundary = (f2 - f1) / cellSize;
      const caustic = Math.pow(Math.max(0, 1 - boundary * 5), 2.5);
      const idx = (y * size + x) * 4;
      const v = Math.floor(caustic * 255);
      data[idx] = v;
      data[idx + 1] = v;
      data[idx + 2] = v;
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  _sharedCausticTexture = tex;
  return tex;
}

// ── Water shader ─────────────────────────────────────────────────

const WATER_VERT = /* glsl */ `
  varying vec2 vLocalXY;

  void main() {
    vLocalXY = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const WATER_FRAG = /* glsl */ `
  precision highp float;

  uniform float     uTime;
  uniform sampler2D uCaustic;
  uniform vec2      uCardSize;
  uniform vec3      uColorDeep;
  uniform vec3      uColorBase;
  uniform vec3      uColorCaustic;
  uniform vec3      uColorMeniscus;

  varying vec2 vLocalXY;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 p = vLocalXY;

    // ── Organic puddle mask ──
    float warpLarge = vnoise(p * 0.18 + vec2(uTime * 0.04, uTime * 0.05));
    float warpSmall = vnoise(p * 0.55 + vec2(uTime * 0.07, uTime * 0.06));
    float radiusWarp = (warpLarge - 0.5) * 2.2 + (warpSmall - 0.5) * 0.8;
    float warpedRadial = max(0.0, length(p) + radiusWarp);
    float puddleMask = exp(-warpedRadial * warpedRadial * 0.045);

    // Card silhouette clip. Hard-clip to the card outline.
    vec2 q = abs(p) - uCardSize * 0.5;
    float cardSdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
    float cardClip = smoothstep(0.1, -0.1, cardSdf);
    puddleMask *= cardClip;
    if (puddleMask < 0.008) discard;

    // ── DOUBLE-SCROLLING CAUSTIC with CHROMATIC ABERRATION ──
    //
    // Per-channel offset gives caustic peaks an iridescent fringe.
    // R/G/B don't perfectly overlap at the edges of bright veins,
    // producing rainbow chromatic edges like real underwater light.
    vec2 uv1 = p * 0.085 + vec2(uTime * 0.028, uTime * 0.022);
    vec2 uv2 = p * 0.062 - vec2(uTime * 0.024,-uTime * 0.030);

    const vec2 caR = vec2( 0.0035,  0.0018);
    const vec2 caB = vec2(-0.0035, -0.0018);

    vec3 c1 = vec3(
      texture2D(uCaustic, uv1 + caR).r,
      texture2D(uCaustic, uv1      ).r,
      texture2D(uCaustic, uv1 + caB).r
    );
    vec3 c2 = vec3(
      texture2D(uCaustic, uv2 - caR).r,
      texture2D(uCaustic, uv2      ).r,
      texture2D(uCaustic, uv2 - caB).r
    );
    vec3 causticRGB = min(c1, c2);
    causticRGB = pow(causticRGB, vec3(1.4));
    float causticAvg = (causticRGB.r + causticRGB.g + causticRGB.b) / 3.0;

    // ── SPARKLE PINPOINTS ──
    //
    // pow(c1.g * c2.g, 14) is near-zero everywhere except the rare
    // pixels where both scrolling caustic samples happen to peak
    // together. As the two scrolls drift past each other these
    // intersection points move around. Micro-star twinkling.
    float sparkleRaw = c1.g * c2.g;
    float sparkle = pow(sparkleRaw, 14.0) * 4.0;

    // ── MENISCUS RIM with FOAM ──
    //
    // Gaussian band at the puddle's effective radius gives the
    // basic surface-tension brightness. ON TOP of that, a high-
    // frequency vnoise gated by the same band adds tiny irregular
    // foam highlights. Surface-turbulence speckle at the edge.
    float edgeDist = abs(warpedRadial - 5.0);
    float meniscusCore = exp(-edgeDist * edgeDist * 0.45) * 0.35;
    float foamN = vnoise(p * 3.2 + vec2(uTime * 0.55, uTime * 0.35));
    float foam = exp(-edgeDist * edgeDist * 0.55)
               * smoothstep(0.55, 0.85, foamN) * 0.45;
    float meniscus = meniscusCore + foam;

    // ── WET DARKEN ──
    //
    // Water absorbs light, so the card art should read slightly
    // darker through the puddle. Modelled as a small extra alpha
    // contribution that biases the colour mix toward uColorDeep at
    // low caustic. Gives the puddle a sense of depth/material.
    float wetDarken = 0.10 * (1.0 - causticAvg);

    // ── Density composition ──
    float waterBase = 0.30;
    float density = (waterBase + wetDarken + causticAvg * 0.55 + sparkle + meniscus) * puddleMask;
    if (density < 0.004) discard;

    // ── Colour ──
    vec3 col = mix(uColorDeep, uColorBase, smoothstep(0.0, 0.5, causticAvg));
    vec3 causticHue = uColorCaustic + (causticRGB - vec3(causticAvg)) * 2.2;
    col = mix(col, causticHue, causticAvg);
    col += vec3(1.0) * sparkle;
    col = mix(col, uColorMeniscus, clamp(meniscus * 1.4, 0.0, 1.0));

    gl_FragColor = vec4(col, density);
  }
`;

export function createSubmergeWaterEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: Math.random() * 100 },
      uCaustic: { value: getCausticTexture() },
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uColorDeep:     { value: new THREE.Color(0x1a3850) },
      uColorBase:     { value: new THREE.Color(0x3a6e94) },
      uColorCaustic:  { value: new THREE.Color(0xb8e4f0) },
      uColorMeniscus: { value: new THREE.Color(0xdcf2fa) },
    },
    vertexShader: WATER_VERT,
    fragmentShader: WATER_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET;
  mesh.renderOrder = 18;
  group.add(mesh);

  return { points: group, materials: [material], geometry };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateSubmergeWaterEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.materials) {
    material.uniforms.uTime.value += dt;
  }

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeSubmergeWaterEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
